import { type CompanyContactId, Dtos, type SuretyAccountId } from "@inrev/common";
import { Option } from "@inrev/core";
import {
	useCreateAccountCompany,
	useDeleteAccountCompany,
	useFetchAccountCompanies,
	useFetchSuretyAccount,
	useUpdateAccountCompany,
} from "@portal/api/account";
import { CompanyContactCard } from "@portal/components/features/contact-cards";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { Text } from "@portal/components/primitives";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { FC, useMemo, useState } from "react";
import type { z } from "zod";
import { SectionHeaderWithAdd } from "../../../../account/admin/tabs/details/SectionHeaderWithAdd";
import { AccountCompanyModal } from "../../create/components/AccountCompanyModal";

type AccountCompaniesSectionProps = {
	accountId: SuretyAccountId;
};

type CompanyFormData = z.infer<typeof Dtos.Company.Create.FromSubmission.Request.schema>;

export const AccountCompaniesSection: FC<AccountCompaniesSectionProps> = ({ accountId }) => {
	const { suretyAccount, suretyAccountIsLoading, suretyAccountError, refetchSuretyAccount } =
		useFetchSuretyAccount(accountId);
	const { companies, companiesIsLoading, companiesError, refetchAccountCompanies } =
		useFetchAccountCompanies(accountId);

	const { createAccountCompany } = useCreateAccountCompany(accountId);
	const { updateAccountCompany } = useUpdateAccountCompany(accountId);
	const { deleteAccountCompany } = useDeleteAccountCompany(accountId);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingCompanyId, setEditingCompanyId] = useState<CompanyContactId | null>(null);

	const editingCompany = useMemo(() => {
		if (!editingCompanyId) return undefined;
		const companiesList = companies || [];
		return companiesList.find((c) => c.id === editingCompanyId);
	}, [editingCompanyId, companies]);

	if (suretyAccountIsLoading || companiesIsLoading) {
		return (
			<div className="h-[400px]">
				<CenteredSpinner />
			</div>
		);
	}

	if (suretyAccountError) {
		return (
			<ReportedRetryableLoadError
				error={suretyAccountError}
				resource="account"
				onRetry={refetchSuretyAccount}
			/>
		);
	}

	if (!suretyAccount) {
		return <CenteredSpinner />;
	}

	if (companiesError) {
		return (
			<ReportedRetryableLoadError
				error={companiesError}
				resource="account companies"
				onRetry={refetchAccountCompanies}
			/>
		);
	}

	const isClosed = suretyAccount.status === "closed";
	const isReadOnly = isClosed || !!suretyAccount.indemnityAgreementId;
	const companiesList = companies || [];
	const primaryCompanyId = suretyAccount.primaryCompanyId;

	const handleAddCompany = () => {
		setEditingCompanyId(null);
		setIsModalOpen(true);
	};

	const handleEditCompany = (companyId: CompanyContactId) => {
		setEditingCompanyId(companyId);
		setIsModalOpen(true);
	};

	const handleDeleteCompany = (companyId: CompanyContactId) => {
		if (companiesList.length <= 1) {
			return;
		}

		deleteAccountCompany({ companyId });
	};

	const handleSubmitCompany = (formData: CompanyFormData) => {
		const onSuccess = () => {
			setIsModalOpen(false);
			setEditingCompanyId(null);
		};

		if (editingCompanyId) {
			updateAccountCompany({
				companyId: editingCompanyId,
				data: formData,
				onSuccess,
			});
		} else {
			createAccountCompany({
				data: formData,
				onSuccess,
			});
		}
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setEditingCompanyId(null);
	};

	return (
		<>
			<div data-testid="agent-account-companies-section" className="w-full flex flex-col space-y-5">
				{isReadOnly ? (
					<Text type="heading" color="primary">
						Companies
					</Text>
				) : (
					<SectionHeaderWithAdd
						title="Companies"
						addLabel="Add Company"
						testId="add-company"
						onAdd={handleAddCompany}
					/>
				)}
				<div className="w-full flex flex-wrap gap-[15px]">
					{companiesList.length === 0 ? (
						<Text type="body" color="secondary">
							None
						</Text>
					) : (
						companiesList.map((company) => {
							const isPrimary = company.id === primaryCompanyId;
							const canModify = !isReadOnly && !isPrimary;
							const canAddAddress = !isClosed && Option.isNone(company.address);

							return (
								<CompanyContactCard
									key={company.id}
									company={company}
									onEdit={
										canModify || canAddAddress ? () => handleEditCompany(company.id) : undefined
									}
									onDelete={canModify ? () => handleDeleteCompany(company.id) : undefined}
								/>
							);
						})
					)}
				</div>
			</div>

			{isModalOpen && (
				<AccountCompanyModal
					isOpen={isModalOpen}
					onClose={handleCloseModal}
					onSubmit={handleSubmitCompany}
					existingNames={companies
						?.filter((company) => company.name !== editingCompany?.name)
						.map((company) => company.name)}
					initialData={
						editingCompany
							? {
									name: editingCompany.name,
									address: editingCompany.address,
									fein: editingCompany.fein ?? null,
									isPrimaryCompany: editingCompany?.id === primaryCompanyId,
								}
							: {
									name: "",
									address: null,
									fein: null,
									isPrimaryCompany: false,
								}
					}
					hidePrimaryCompanyOption={true}
					addressOnly={
						Option.isSome(editingCompany) && (isReadOnly || editingCompany.id === primaryCompanyId)
					}
				/>
			)}
		</>
	);
};
